import React from 'react'

const BarracudaPolicy: React.FC<any> = () => {
  return (
    <div id="pageBody" className="container">
      <p>
        <i>Last Updated: December 2019</i>
      </p>
      <p>
        Barracuda Networks, Inc., and its affiliated companies worldwide (collectively “Barracuda”, “we” or “our”)
        recognize and respect your right to privacy.
      </p>
      <p>
        This Privacy Policy explains how Barracuda collects, shares, stores and uses data. Typically, we collect
        customer data in order to provide the products and services that customers purchase or request from us. For
        example, we may capture email addresses so that a customer can check the status of its software or open a case
        with our tech support team. We collect visitor data through the Website in order to understand and improve the
        Website content, as well as maintain a secure computing environment. We collect marketing data in order to
        respond to inquiries about our products and services and provide access to the latest topical information that
        potential customers might be interested in.
      </p>
      <p>
        When we collect data, we do so in compliance with this Privacy Policy, which describes how we handle the data we
        collect in more detail. This Privacy Policy also describes other important topics relating to information
        privacy.
      </p>

      <p>
        <b>Please read this Privacy Policy carefully to understand how we&nbsp;handle your Personal Information.</b>
      </p>
      <p>
        By accessing or browsing&nbsp;
        <a href="https://www.barracuda.com">www.barracuda.com</a>&nbsp; (the “<b>Website</b>”) and/or using any of the
        services that we provide to you or that you access, you confirm that you have read and understood the entirety
        of this Privacy Policy.
      </p>

      <p>
        Barracuda complies with the EU-U.S. Privacy Shield Framework and Swiss-U.S. Privacy Shield Framework as set
        forth by the U.S. Department of Commerce regarding the collection, use and retention of personal information
        transferred from the European Union and Switzerland to the United States. Barracuda has certified to the
        Department of Commerce that it adheres to the Privacy Shield Principles. If there is any conflict between the
        terms in this Privacy Policy and the Privacy Shield Principles, the Privacy Shield Principles shall govern. To
        learn more about the Privacy Shield program please visit&nbsp;
        <a href="https://www.privacyshield.gov">https://www.privacyshield.gov</a>. To view our certification, please
        visit:&nbsp;
        <a href="https://www.privacyshield.gov/participant?id=a2zt0000000TQk6AAG&amp;status=Active">
          https://www.privacyshield.gov/participant?id=a2zt0000000TQk6AAG&amp;status=Active.
        </a>
      </p>
      <h3>1. Types of Information Collected.</h3>
      <p>
        We may collect and process any of the following information about you, which we refer to as “
        <b>Personal Information</b>” throughout this Privacy Policy:
      </p>
      <h4>
        <b>
          <i>1.1 Information You Give Us.</i>
        </b>
      </h4>
      <p>
        You may give us information about yourself by using the online forms provided on the Website or by contacting us
        by phone, e-mail or other means. This includes, for example, filling in the “Contact Us” form on the Website or
        when you provide your information to us in order to receive our services.
      </p>

      <p>
        The information you give us may include email address, name, mailing address, telephone number, company name,
        company address, credit card information, job title, account information and any updates to information provided
        to us.
      </p>
      <p>
        Please note that we need certain types of information so that we can provide services to you.&nbsp;If you do not
        provide us with such information, or if you ask us to delete it, you may no longer be able to access our
        services.
      </p>
      <h4>
        <b>
          <i>1.2 Information We May Collect About You.</i>
        </b>
      </h4>
      <p>We may automatically collect any of the following information each time you visit the Website:</p>
      <ul>
        <p />
        <li>
          Technical information, including the Internet Protocol (IP) address used to connect your computer to the
          Internet, domain name and country that requests information, the file(s) requested, browser type and version,
          browser plug-in types and versions, operating system and platform; and
        </li>
        <p />
        <p />
        <li>
          Information about your visit, including the full Uniform Resource Locators (URL) clickstream to, through and
          from the Website (including date and time), length of visits to certain pages, page interaction information
          (such as scrolling, clicks, and mouse-overs) and methods used to browse away from the page.
        </li>
        <p />
      </ul>
      <h4>
        <b>
          <i>1.3 Information We May Receive From Other Sources.</i>
        </b>
      </h4>
      <p>We will receive information about you if:</p>
      <ul>
        <p />
        <li>
          You obtain our services through one of our resellers or partners. The types of information that we may receive
          are the same as the information that you may give to us detailed in section 1.1 above.
        </li>
        <p />
        <p />
        <li>
          You use any of the other websites we operate or the other services we provide. In this case, we will inform
          you when we collect that data that it may be shared internally and combined with data collected on the
          Website. We work closely with third parties (including, for example, advertising networks, and analytics and
          search information providers) and may receive information about you from them.
        </li>
        <li>
          Some Barracuda products use Google Analytics for purposes of improving product performance. For more
          information on Google Analytics, and how it collects and processes data visit the site “How Google Uses
          Information From Sites or Apps that Use our Services,” located at&nbsp;
          <a href="https://policies.google.com/technologies/partner-sites">
            https://policies.google.com/technologies/partner-sites.
          </a>
        </li>
      </ul>
      <p />

      <h3>2. Use of Information.</h3>
      <p />
      <p>
        We, or third-party data processors acting on our behalf, collect, use and store the Personal Information listed
        above. We will use your Personal Information in order to deliver your contracted services, in order to comply
        with applicable laws, where we have obtained your consent, or where it is in the legitimate interests of
        Barracuda to handle your Personal Information. More specifically, we collect, use and store your Personal
        Information for the following reasons:
      </p>
      <ul>
        <li>To register you for webinars/seminars.</li>

        <li>To assign a password.</li>

        <li>To ensure that content from the Website and services is presented in the most efficient manner for you.</li>

        <li>
          To provide you with information, products or services that you request from us or which we feel may interest
          you, only where you have consented to be contacted for such purposes.
        </li>

        <li>To carry out our obligations arising from any contracts entered into between you and us.</li>
        <p />
        <li>To allow you to participate in interactive features (e.g., live chat) when you choose to do so.</li>

        <li>
          To notify you about changes to our products or services and to keep you informed about our fees and charges.
        </li>

        <li>To provide the service(s) you have requested.</li>

        <li>To improve the quality and accuracy of the services.</li>

        <li>To allow you to access and use the Website and to register for an account.</li>

        <li>
          To carry out activities in the legitimate interests of Barracuda, for example, pursuing debt or ensuring the
          security of our services and the Website.
        </li>

        <li>To verify your identity in order to prevent and detect money laundering and fraud.</li>

        <li>To carry out statistical analysis and market research.</li>

        <li>For marketing, advertising and promotional purposes.</li>

        <li>
          For the purposes of improving and maintaining the Website, preparing reports or compiling statistics in order
          to improve our services. Such details will be anonymized as much as reasonably possible, and you will not be
          identifiable from the data collected.
        </li>

        <li>To take other action you request when you supply the Personal Information.</li>
      </ul>
      <p>Personal Information is not used to profile those who access the Website.</p>
      <h3>3. Data Retention.</h3>
      <p>
        We retain Personal Information for as long as you use the services we provide and then as required to comply
        with applicable laws.
      </p>
      <p />
      <h3>4. Disclosure of Information.</h3>
      <p>
        We will not sell, hire, lease or rent your Personal Information that we collect to any third party without
        notifying you and/or obtaining your consent, except as expressly set forth in this section. Where you have given
        your consent for us to use your information in a particular way, but later change your mind, you must contact us
        as set forth in the Contact Us section of this Privacy Policy in order to revoke such consent.
      </p>
      <h4>
        <b>
          <i>4.1 Barracuda Affiliate Companies.</i>
        </b>
      </h4>
      <p>
        We will share your Personal Information with any company that is a member of our group, including those that are
        listed in this Privacy Policy.
      </p>
      <p />

      <h4>
        <b>
          <i>4.2 Categories of Third Parties.</i>
        </b>
      </h4>

      <p>
        Any third parties with whom we share your Personal Information are limited (by law and by contract) in their
        ability to use your Personal Information for any purpose other than to provide services for us. We will always
        ensure that any third parties with whom we share your Personal Information are subject to privacy and security
        obligations consistent with this Privacy Policy and applicable laws. We will share your Personal Information
        with the following categories of third parties:
      </p>
      <p />
      <ul>
        <li>
          Our service providers and subcontractors including, but not limited to, payment processors, suppliers of
          technical and support services and cloud service providers;
        </li>

        <li>Companies that assist us in our marketing, advertising and promotional activities;</li>

        <li>
          Analytics and search engine providers that assist us in the improvement and optimization of the Website; and
        </li>

        <li>Systems integrators and service providers who resell our products and services.</li>
      </ul>
      <p />
      <h4>
        <b>
          <i>4.3 Other Third Party Disclosures.</i>
        </b>
      </h4>
      <p>We will also disclose your Personal Information to third parties:</p>
      <ul>
        <li>
          In the event we sell or buy any business or assets, in which case we may disclose your Personal Information to
          the prospective seller or buyer of such business or asset;
        </li>

        <li>
          If Barracuda, or substantially all of its assets, is acquired by a third party, in which case information held
          by it about its customers and partners will be one of the transferred assets;
        </li>
        <li>
          If Barracuda is under a duty to disclose or share your Personal Information in order to comply with any legal
          obligation or any lawful request from government or law enforcement officials, and as may be required to meet
          national security or law enforcement requirements or prevent illegal activity;
        </li>

        <li>
          In order to enforce or apply Barracuda’s terms and conditions or any other agreement or to respond to any
          claims, to protect our rights or the rights of a third party, to protect the safety of any person or to
          prevent any illegal activity; or
        </li>

        <li>
          To protect the rights, property or safety of Barracuda, our customers or other persons. This may include
          exchanging information with other organizations for the purposes of fraud protection and credit risk
          reduction.
        </li>
        <p />
      </ul>
      <h3>5. Protection of Personal Information.</h3>
      <p>
        Barracuda is committed to taking steps to protect Personal Information you provide to us, including
        administrative, technical and physical measures to safeguard Personal Information against loss, theft, misuse,
        unauthorized access, disclosure, alteration and destruction.
      </p>
      <p>
        Personal Information is only accessed by authorized staff who are instructed to handle Personal Information in a
        confidential manner. Personal Information is protected by firewalls and standard industry security measures. We
        may also transmit sensitive information and transactional data using secure socket layer (SSL) encryption.
      </p>
      <p>
        The transmission of information through the Internet is not completely secure. Although we will do our best to
        protect Personal Information, we cannot guarantee the security of your data transmitted to the Website; any
        transmission is at your own risk. Once we have received your Personal Information, we will use strict procedures
        and security features to try to prevent unauthorized access.
      </p>
      <p>
        Where we have given you (or where you have chosen) a password that enables you to access certain parts of the
        Website or our products and services, you are responsible for keeping this password confidential. We ask you not
        to share a password with anyone.
      </p>

      <h3>6. Your Rights.</h3>
      <p>
        Subject to the conditions set forth under applicable law, you have the right to request to access, review,
        correct, update, suppress, restrict or delete Personal Information that you have provided to us. You have the
        right to request an electronic copy of Personal Information for purposes of transmitting it to another company.
        You have the right to not be subject to a decision based solely on automated&nbsp;processing, including
        profiling. You may submit such requests by sending an email to&nbsp;
        <a href="mailto:legal@barracuda.com">legal@barracuda.com</a>. We will respond to your request in accordance with
        applicable law. In your request, you must advise what Personal Information you would like to access, review,
        correct, update, suppress, restrict or delete; or otherwise let us know what limitations you would like to put
        on our use of your Personal Information.
      </p>
      <p>
        Please note that we may need to retain certain Personal Information for recordkeeping purposes and/or to
        complete transactions that you began prior to requesting a change or deletion. In the event your Personal
        Information is processed on the basis of your consent, you may withdraw consent at any time by sending an email
        to&nbsp;
        <a href="mailto:legal@barracuda.com">legal@barracuda.com</a>
        and specifying the details of your request. However, any withdrawal of consent will not affect the lawfulness of
        any processing based on consent before it is withdrawn.
      </p>
      <p>
        You may lodge a complaint with a supervisory authority, in particular in your Member State of residence, if you
        consider that the collection and use of your Personal Information infringes this Privacy Policy or applicable
        law. You have the right to ask us to not process your Personal Information for marketing purposes. We will
        inform you before collecting your Personal Information if we intend to use your Personal Information for such
        purposes or if we intend to disclose your information to any third party for such purposes. You can exercise
        your right to prevent such processing by checking certain boxes on the forms we use to collect your Personal
        Information. Alternatively, you can contact us by email at&nbsp;
        <a href="mailto:legal@barracuda.com">legal@barracuda.com</a>&nbsp; at any time.
      </p>
      <p>
        If you have previously given us consent to use your Personal Information for marketing purposes, you can choose
        to stop receiving marketing communications that we send to you from time to time by following the unsubscribe
        instructions included in such communications. Alternatively, you can opt out of such communications at any time
        by contacting us by email at&nbsp;
        <a href="mailto:legal@barracuda.com">legal@barracuda.com.</a>
      </p>
      <p>
        You can choose to opt-out from your Personal Information being used for any purpose that differs from the
        purposes notified to you when we collected your personal information or from being disclosed to any third party.
      </p>
      <p>
        If you would like to opt out, please contact us as set forth in the Contact Us section of this Privacy Policy.
        <b>
          Please note that opting-out may prevent us from providing you with our services or information requested by
          you.
        </b>
      </p>
      <h3>7. Transfers of Information.</h3>
      <p>
        With respect to Personal Information transferred from the European Economic Area (“<b>EEA</b>”) or Switzerland
        to Barracuda, we will process Personal Information in accordance with applicable privacy laws and the EU-US
        Privacy Shield Framework or the Swiss-US Privacy Shield Framework, as applicable (each and together, the &quot;
        <b>Privacy Shield</b>&quot;).
      </p>
      <p>
        If you are located outside of the United States, the Personal Information that Barracuda collects from you may
        be transferred to and stored in the United States or another location outside of the EEA or Switzerland. Any
        transfer of Personal Information from the EEA or Switzerland to the United States and any onward data transfer
        will be in compliance with Privacy Shield, details of which are set forth below in section 7.1.
      </p>
      <p>
        Your Personal Information may also be processed by staff operating in the United States or outside the EEA or
        Switzerland that are working for us, other members of our group or third-party data processors. Such staff may
        be engaged in, among other things, the provision of our services to you, the processing of transactions and/or
        the provision of support services. By providing us with your Personal Information you acknowledge and agree to
        any such transfer, storage and processing.
      </p>
      <p>
        We will always take steps to ensure that such transfers comply with applicable privacy laws, and we will take
        all reasonable precautions to ensure that your Personal Information is treated securely and in accordance with
        this Privacy Policy. However, you should be aware that countries outside the EEA or Switzerland may not offer
        the same level of protection for Personal Information.
      </p>
      <h4>
        <b>
          <i>7.1 Privacy Shield.</i>
        </b>
      </h4>
      <p>
        Barracuda is an international business with multi-national operations, systems and processes. If you are located
        outside the United States and provide Personal Information to us, Barracuda may transfer your Personal
        Information to the United States and use it there in accordance with this Privacy Policy.
      </p>
      <h4>
        <b>
          <i>7.2 Our Obligations to You Under the Privacy Shield.</i>
        </b>
      </h4>
      <p />
      <ul>
        <li>
          Barracuda has certified that it complies with the EU-U.S. Privacy Shield Framework and the Swiss-U.S. Privacy
          Shield Framework as set forth by the U.S. Department of Commerce and approved by the European Commission
          regarding the collection, use and retention of Personal Information transferred from the European Union and
          Switzerland to the United States, respectively.
        </li>
        <li>Barracuda has certified to the Department of Commerce that it adheres to the Privacy Shield Principles.</li>
        <li>Barracuda collects Personal Information in accordance with this Privacy Policy.</li>

        <li>
          If there is any conflict between this Privacy Policy and the Privacy Shield Principles, the Privacy Shield
          Principles shall govern. To learn more about the Privacy Shield program please visit&nbsp;
          <a href="https://www.privacyshield.gov">https://www.privacyshield.gov</a>. To view our certification, please
          visit&nbsp;
          <a href="https://www.privacyshield.gov/participant?id=a2zt0000000TQk6AAG&amp;status=Active">
            https://www.privacyshield.gov/participant?id=a2zt0000000TQk6AAG&amp;status=Active.
          </a>
        </li>
        <li>The Federal Trade Commission has jurisdiction over Barracuda’s compliance with the Privacy Shield.</li>
        <li>
          Barracuda commits to cooperate with EU Data Protection Authorities and the Swiss Federal Data Protection and
          Information Commissioner and to comply with the advice given to Barracuda by such authorities with regard to
          human resources data transferred from the EU and Switzerland in the context of the employment relationship.
        </li>
      </ul>
      <p />

      <h4>
        <b>
          <i>7.3 Accountability and Liability for Onward Transfer.</i>
        </b>
      </h4>

      <p>
        Barracuda may be liable in cases of onward transfer of EU and Swiss individuals’ data to third parties received
        pursuant to the Privacy Shield.
      </p>

      <h4>
        <b>
          <i>7.4 Privacy Complaints, Recourse, Enforcement and Liability.</i>
        </b>
      </h4>

      <p>
        In compliance with the Privacy Shield Principles, Barracuda commits to resolve complaints about your privacy and
        our collection or use of your Personal Information without charge to you.
      </p>
      <p>
        European Union and Swiss individuals with inquiries or complaints regarding this Privacy Policy should first
        contact us at&nbsp;
        <a href="mailto:legal@barracuda.com">legal@barracuda.com.</a>
      </p>
      <p>
        Barracuda has committed to refer all unresolved privacy complaints to JAMS, an alternative dispute resolution
        provider based in the US. If you do not receive timely acknowledgment of your complaint, or if your complaint is
        not satisfactorily addressed, please visit&nbsp;
        <a href="https://www.jamsadr.com">https://www.jamsadr.com</a>
        or
        <a href="https://www.jamsadr.com/eu-us-privacy-shield">https://www.jamsadr.com/eu-us-privacy-shield</a>&nbsp;
        for more information or to file a complaint. The services of JAMS are provided to you at no cost.
      </p>
      <p>
        Please note that if your complaint is not resolved through the above mechanism, in certain limited circumstances
        you may be able to invoke a binding arbitration proceeding before the Privacy Shield Panel if Barracuda has
        failed to meet its obligations to you under the Privacy Shield and that failure has still not been remedied.
        Additional information about invoking binding arbitration is available online at the following link:&nbsp;
        <a href="https://www.privacyshield.gov/article?id=ANNEX-I-introduction">
          https://www.privacyshield.gov/article?id=ANNEX-I-introduction.
        </a>
      </p>
      <h4>
        <b>
          <i>7.5 Barracuda Networks Companies in the EU and Switzerland.</i>
        </b>
      </h4>
      <p>
        The following Barracuda companies collect, process, transfer and store Personal information in accordance with
        Privacy Shield. The relevant Barracuda company involved will depend on the country in which you are based.
      </p>
      <p>
        3SP Ltd (UK); Barracuda Networks AG (Austria); Eduard-Bodem-Gasse 1A-6020 (Innsbruck); Barracuda Networks BV
        (Belgium); Zweigniederlassung Deutschland (Germany); Sede Secondria Italiana (Italy); Barracuda Netherlands BV;
        Barracuda Networks AG (Switzerland); Barracuda Networks Limited (UK); and Barracuda Networks, Inc. (U.S.)
      </p>
      <h3>8. Use of Cookies.</h3>

      <h4>
        <b>
          <i>8.1 What Are Cookies and Related Technologies</i>
        </b>
      </h4>
      <p>
        Cookies are small data files that are stored on your computer that allow us and our third-party partners and
        providers to collect certain information about your interactions with our email communications, websites and
        other online services. We and our third-party partners and providers may also use other, related technologies to
        collect this information, such as web beacons, pixels, embedded scripts, location-identifying technologies and
        logging technologies (collectively, “cookies”).
      </p>

      <h4>
        <b>
          <i>8.2 What We Collect When Using Cookies</i>
        </b>
      </h4>
      <p>
        We and our third-party partners and providers may use cookies to automatically collect certain types of usage
        information when you visit or interact with our email communications, websites and other online services. For
        example, we may collect log data about your device and its software, such as your IP address, operating system,
        browser type, date/time of your visit, and other similar information. We may collect analytics data or use
        third-party analytics tools such as Google Analytics to help us measure usage and activity trends for our online
        services and better understand our customer base.
      </p>
      <p>
        To view the types of cookies Barracuda uses on the Website, please&nbsp;
        <a
          href="https://assets.barracuda.com/assets/docs/dms/BN-CookieManagementTool-121219-1542-252.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          click here
        </a>
        .
      </p>

      <h4>
        <b>
          <i>8.3 How We Use That Information</i>
        </b>
      </h4>
      <p>
        We use or may use the data collected through cookies to: (a) remember information so that you will not have to
        re-enter it during your visit or the next time you visit our websites and online services; (b) provide and
        monitor the effectiveness of our websites and online services; (c) monitor online usage and activities of our
        websites and online services; (d) diagnose errors and problems with our websites and online services; (e)
        otherwise plan for and enhance our online services; and (f) facilitate the purposes identified in this Privacy
        Policy. We and our advertising partners also use the information we collect through cookies to understand your
        browsing activities, including across unaffiliated third-party sites, so that we can deliver ads and information
        about products and services that may be of interest to you.
      </p>
      <p>
        Please note that we link some of the personal information we collect through cookies with the other personal
        information that we collect about you and for the purposes described in this Privacy Policy.
      </p>

      <h4>
        <b>
          <i>8.4 Your Choices About Cookies</i>
        </b>
      </h4>
      <p>
        If you would prefer not to accept cookies, most browsers will allow you to: (i) change your browser settings to
        notify you when you receive a cookie, which lets you choose whether or not to accept it; (ii) disable existing
        cookies; or (iii) set your browser to automatically reject cookies. Please note that doing so may negatively
        impact your experience using our online services, as some features and services on our online services may not
        work properly. Depending on your device and operating system, you may not be able to delete or block all
        cookies. In addition, if you want to reject cookies across all your browsers and devices, you will need to do so
        on each browser on each device you actively use. You may also set your email options to prevent the automatic
        downloading of images that may contain technologies that would allow us to know whether you have accessed our
        email and performed certain functions with it.
      </p>
      <p>
        Some Barracuda products use Google Analytics for purposes of improving product performance. For more information
        on Google Analytics, and how it collects and processes data visit the site “How Google Uses Information From
        Sites or Apps that Use our Services,” located at&nbsp;
        <a href="https://policies.google.com/technologies/partner-sites">
          https://policies.google.com/technologies/partner-sites
        </a>
        .
      </p>

      <h4>
        <b>
          <i>8.5 Your Choices About Online Ads</i>
        </b>
      </h4>
      <p>
        We support the self-regulatory principles for online behavioral advertising (Principles) published by the
        Digital Advertising Alliance (DAA). This means that we allow you to exercise choice regarding the collection of
        information about your online activities over time and across third-party websites for online interest-based
        advertising purposes. More information about these Principles can be found at&nbsp;
        <a href="www.aboutads.info" target="_blank">
          www.aboutads.info
        </a>
        . If you want to opt out of receiving online interest-based advertisements on your internet browser from
        advertisers and third parties that participate in the DAA program and perform advertising-related services for
        us and our partners, please follow the instructions at&nbsp;
        <a href="www.aboutads.info/choices" target="_blank">
          www.aboutads.info/choices
        </a>
        , or&nbsp;
        <a href="http://www.networkadvertising.org/choices/" target="_blank" rel="noopener noreferrer">
          http://www.networkadvertising.org/choices/
        </a>
        &nbsp; to place an opt-out cookie on your device indicating that you do not want to receive interest-based
        advertisements. Opt-out cookies only work on the internet browser and device they are downloaded onto. If you
        want to opt out of interest-based advertisements across all your browsers and devices, you will need to opt out
        on each browser on each device you actively use. If you delete cookies on your device generally, you will need
        to opt out again.
      </p>

      <p>
        If you want to opt out of receiving online interest-based advertisements on mobile apps, please follow the
        instructions at&nbsp;
        <a href="http://www.aboutads.info/appchoices" target="_blank" rel="noopener noreferrer">
          http://www.aboutads.info/appchoices
        </a>
        .
      </p>

      <p>
        Please note that when you opt out of receiving interest-based advertisements, this does not mean you will no
        longer see advertisements from us or on our online services. It means that the online ads that you do see from
        DAA program participants should not be based on your interests. We are not responsible for the effectiveness of,
        or compliance with, any third-parties’ opt-out options or programs or the accuracy of their statements regarding
        their programs. In addition, third parties may still use cookies to collect information about your use of our
        online services, including for analytics and fraud prevention as well as any other purpose permitted under the
        DAA’s Principles.
      </p>

      <h3>9. Third Party Websites.</h3>

      <p>
        The Website may, from time to time, contain links to other websites operated by third parties. Please note that
        this Privacy Policy applies solely to the information collected from the Website, and we cannot be responsible
        for Personal Information collected and stored by third parties. If you choose to visit any websites operated by
        third parties, then their privacy policies would apply, and you should carefully read such third parties’
        privacy policies before submitting any Personal Information to those websites. We do not endorse or otherwise
        accept any responsibility or liability for the content of such third-party websites, terms and conditions or
        policies.
      </p>
      <h3>10. Changes to the Privacy Policy.</h3>
      <p>
        We may update this Privacy Policy from time to time. By continuing to use the services and the Website, you
        agree to the latest version of this Privacy Policy. Any future changes we make to this Privacy Policy will be
        posted on this page and, where appropriate, you will be notified of such changes via email. Please visit this
        page frequently to check for any updates or changes to this Privacy Policy.
      </p>
      <p />
      <p>
        If you have any questions regarding this Privacy Policy or our use of Personal Information on the Website,
        please send us your questions or comments as set forth in section 11 below.
      </p>
      <h3>11. Contact Us.</h3>
      <p>
        If you have any questions in relation to this Privacy Policy or how we collect, use or store your Personal
        Information, you can contact us by telephone at: +1 408-342-5400, by mail at Barracuda Networks, Inc., 3175
        South Winchester Boulevard, Campbell, CA 95008, or by email at&nbsp;
        <a href="mailto:legal@barracuda.com">legal@barracuda.com</a>.
      </p>
    </div>
  )
}

export default BarracudaPolicy
